import { Box, Text } from '@wix/design-system';
import { sanitizeHtml } from '@wix/wix-chatbot-common/browser';
import React, { FC, useEffect, useMemo } from 'react';
import { useStore } from 'react-redux';

import { logChatbotGeneralClientLog } from '../../../bi/loggers/logChatbotGeneralClientLog';
import { ILiveChatMessage } from '../../../common/messageCreators';
import { usePingLinkRef } from '../../hooks';
import { useIsRtlHtml } from '../../hooks/useIsRtlHtml';
import { ExtraChatMessageProps } from '../Chat';
import { DirectionWrapper } from '../DirectionWrapper';
import { MessageBubble } from '../MessageBubble';
import { MessageItem } from '../MessageItem';
import { AgentAvatar } from './AgentAvatar';
import { useAttachImageClickHandlers } from './useAttachImageClickHandlers';

type AgentMessageProps = ILiveChatMessage & ExtraChatMessageProps;

export const AgentMessage: FC<AgentMessageProps> = ({ id, agent, html, isFirstInGroup }) => {
  const linkPing = usePingLinkRef<HTMLDivElement>();
  const isRtl = useIsRtlHtml(html);
  const store = useStore();

  useAttachImageClickHandlers(linkPing.current);

  const innerHtmlProps = useMemo(() => ({ __html: sanitizeHtml(html) }), [html]);

  // a special event for debugging. will be removed after CCR-3146 will be resolved
  useEffect(() => {
    logChatbotGeneralClientLog(store.getState(), {
      message: 'Render AgentMessage',
      data: { html: innerHtmlProps.__html, id },
    });
  }, []);

  return (
    <MessageItem id={id}>
      <Box direction="vertical" gap="SP1">
        {isFirstInGroup && (
          <Box gap="SP1" verticalAlign="middle">
            <AgentAvatar agent={agent} />
            <Text size="small" secondary>
              {agent.fullName}
            </Text>
          </Box>
        )}
        <MessageBubble isFirstInGroup={isFirstInGroup}>
          <DirectionWrapper isRtl={isRtl}>
            <div
              ref={linkPing}
              dangerouslySetInnerHTML={innerHtmlProps}
              aria-live="polite"
              role="log"
              data-hook="agent-message"
            />
          </DirectionWrapper>
        </MessageBubble>
      </Box>
    </MessageItem>
  );
};
